import React, { type CSSProperties, type ReactNode } from "react";
import { cx, useKitDisabled } from "./fieldContext";
const PERCENT_SCALE = 100;

/*
 * Schieberegler des Kits (Plan 2026-10-13-antd-ausbau, Stufe 6; Ersatz für Antd `Slider`) auf `<input type="range">`:
 * Tastatur, Bildschirmleser und Touch kommen vom Browser. Der Wert erscheint beim Zeigen, Ziehen und Fokus über
 * dem Griff (`tooltip.formatter`, `null` blendet ihn aus).
 */

export interface SliderProps {
  value?: number;
  defaultValue?: number;
  onChange?: (value: number) => void;
  onChangeComplete?: (value: number) => void;
  /** Antd-Altname für `onChangeComplete`. */
  onAfterChange?: (value: number) => void;
  min?: number;
  max?: number;
  step?: number | null;
  disabled?: boolean;
  tooltip?: { formatter?: ((value?: number) => ReactNode) | null; open?: boolean };
  className?: string;
  style?: CSSProperties;
  id?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
}

/** Antd-Name der Einzelwert-Props. */
export type SliderSingleProps = SliderProps;

export function Slider({ value, defaultValue, onChange, onChangeComplete, onAfterChange, min = 0, max = 100, step = 1, disabled, tooltip, className, style, id, ...aria }: SliderProps) {
  const mergedDisabled = useKitDisabled(disabled);
  const [inner, setInner] = React.useState(defaultValue ?? min);
  const [active, setActive] = React.useState(false);
  const current = value ?? inner;
  const percent = max > min ? ((current - min) / (max - min)) * PERCENT_SCALE : 0;
  const formatter = tooltip?.formatter === undefined ? (v?: number) => v : tooltip.formatter;
  const showTip = formatter !== null && (tooltip?.open ?? active);
  const complete = (next: number) => { onChangeComplete?.(next); onAfterChange?.(next); };
  return (
    <div
      className={cx("ck-slider", mergedDisabled && "ck-slider-disabled", className)}
      style={{ ...style, "--ck-slider-percent": `${percent}%` } as CSSProperties}
      onPointerEnter={() => setActive(true)}
      onPointerLeave={() => setActive(false)}
    >
      <input
        type="range"
        className="ck-slider-input"
        id={id}
        min={min}
        max={max}
        step={step ?? "any"}
        value={current}
        disabled={mergedDisabled}
        {...aria}
        onFocus={() => setActive(true)}
        onBlur={() => setActive(false)}
        onChange={(event) => {
          const next = Number(event.target.value);
          if (value === undefined) setInner(next);
          onChange?.(next);
        }}
        onPointerUp={(event) => complete(Number(event.currentTarget.value))}
        onKeyUp={(event) => complete(Number(event.currentTarget.value))}
      />
      {showTip ? <span className="ck-slider-tooltip" role="presentation">{formatter?.(current)}</span> : null}
    </div>
  );
}
